import type { SiteDto, TransferDetailDto, TransferDto } from '@pl/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { qk, useSiteMap, useSites, useSkuMap, useTransfers } from '../../lib/queries';
import { Empty, ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { useScanSite } from '../useScanSite';
import { kit, plural } from './scanKit';
import { RouteTags } from './transferKit';
import s from './TransferHome.module.css';

/** How far back the Incoming tab still shows a closed transfer: long enough to see last week's shortfall. */
const RECENT_MS = 7 * 24 * 60 * 60 * 1000;

/** "5:42 PM" today, "Sep 22, 5:42 PM" before that — in the site's own time, since that is the clock on its wall. */
function when(iso: string, timeZone: string): string {
  const d = new Date(iso);
  const day = (x: Date) => x.toLocaleDateString('en-US', { timeZone });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone });
  return day(d) === day(new Date()) ? time : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone })}, ${time}`;
}

type Tab = 'incoming' | 'send';

/**
 * Both ends of a transfer from one screen. Incoming is what this site is waiting on, live; Send is
 * this site's drafts, the transfers someone started scanning and has not sent yet.
 *
 * The tab opens on Incoming when something is on its way, because a truck at the door is the one
 * thing here with a clock on it. With nothing on its way it opens on Send if there is a draft to
 * finish, and otherwise stays on Incoming, where last week's shortfalls are. That choice is made
 * once, when both lists have first loaded; after it only a tap moves the tab, so a transfer that
 * arrives on a refresh cannot swap the list out from under a thumb.
 *
 * What is on its way is asked for on its own, never mixed with closed ones under one cap: a
 * transfer sent ahead to a site that is not live yet can be on the road for weeks, and fifty newer
 * closed transfers must not push it off this list while the home badge goes on counting it.
 */
export function TransferHome() {
  const { site } = useScanSite();
  const siteById = useSiteMap();
  const skuById = useSkuMap();
  const tabsId = useId();
  // The same query as the home tile's badge, so the two can never disagree about what is coming.
  const incoming = useTransfers({ siteId: site?.id, direction: 'incoming', status: ['in_transit'] }, Boolean(site?.id));
  const recent = useTransfers({ siteId: site?.id, direction: 'incoming', status: ['closed'], limit: 50 }, Boolean(site?.id));
  const drafts = useTransfers({ siteId: site?.id, direction: 'outgoing', status: ['draft'] }, Boolean(site?.id));
  const [tab, setTab] = useState<Tab | null>(null);

  const onTheWay = useMemo(() => (incoming.data ?? []).filter((t) => t.status === 'in_transit'), [incoming.data]);
  const closed = useMemo(() => {
    const cutoff = Date.now() - RECENT_MS;
    return (recent.data ?? []).filter((t) => t.status === 'closed' && t.closedAt && new Date(t.closedAt).getTime() >= cutoff);
  }, [recent.data]);

  const firstLoaded = incoming.data !== undefined && drafts.data !== undefined;
  useEffect(() => {
    if (tab !== null || !firstLoaded) return;
    setTab(onTheWay.length === 0 && (drafts.data?.length ?? 0) > 0 ? 'send' : 'incoming');
  }, [drafts.data, firstLoaded, onTheWay.length, tab]);

  if (!site) {
    return (
      <div className="scan-body">
        <Empty title="No site on this account" description="Transfers work from a site. Ask an admin to add you to one." />
      </div>
    );
  }

  const shown: Tab = tab ?? 'incoming';
  const tz = site.timezone;

  return (
    <>
      <div className={`scan-body ${s.homeBody}`}>
        <div className={s.tabRow} role="tablist" aria-label="Transfers">
          <button
            type="button"
            role="tab"
            id={`${tabsId}-incoming`}
            aria-selected={shown === 'incoming'}
            aria-controls={`${tabsId}-panel`}
            className={`${s.tabBtn}${shown === 'incoming' ? ` ${s.tabOn}` : ''}`}
            onClick={() => setTab('incoming')}
          >
            Incoming
            {onTheWay.length > 0 && <span className="badge ink">{onTheWay.length}</span>}
          </button>
          <button
            type="button"
            role="tab"
            id={`${tabsId}-send`}
            aria-selected={shown === 'send'}
            aria-controls={`${tabsId}-panel`}
            className={`${s.tabBtn}${shown === 'send' ? ` ${s.tabOn}` : ''}`}
            onClick={() => setTab('send')}
          >
            Send
            {(drafts.data?.length ?? 0) > 0 && <span className="badge">{drafts.data!.length}</span>}
          </button>
        </div>

        <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-${shown}`} className={s.panel}>
          {shown === 'incoming' ? (
            <Listing query={incoming} what="transfers">
              <div className="sect">
                On the way
                <span className="r">
                  <span className="live">
                    <i />
                    Live
                  </span>
                </span>
              </div>
              {onTheWay.length === 0 ? (
                <Empty title="Nothing on its way" description={`No transfers are headed to ${site.name} right now.`} />
              ) : (
                <div className={s.cardList}>
                  {onTheWay.map((t) => (
                    <Link key={t.id} to={`/scan/transfer/${t.id}/in`} className={s.tCard}>
                      <span className={s.cardTop}>
                        <span className={`mono ${s.cardCode}`}>{t.code}</span>
                        <span className="chip">In transit</span>
                        <RouteTags from={siteById.get(t.fromSiteId)} to={siteById.get(t.toSiteId)} />
                      </span>
                      <span className={s.cardSays}>
                        {`${plural(t.totals.sent, 'box', 'boxes')} from ${siteById.get(t.fromSiteId)?.name ?? 'another site'}, sent ${t.sentAt ? when(t.sentAt, tz) : 'just now'}`}
                        {t.totals.received > 0 && <span className={s.cardSoFar}>{` · ${t.totals.received} scanned in`}</span>}
                      </span>
                      <SkuLines transfer={t} count={(l) => l.sent} skuById={skuById} />
                    </Link>
                  ))}
                </div>
              )}

              {/* Its own request since in-transit was split off, so its own first-load failure: without
                  this a list that never loaded reads as "nothing closed this week". */}
              {recent.isLoadingError ? (
                <>
                  <div className="sect">Closed this week</div>
                  <div className={kit.tkStack}>
                    <ErrorNote error={recent.error} />
                    <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void recent.refetch()}>
                      Try again
                    </button>
                  </div>
                </>
              ) : (
                <RefetchNote queries={[recent]} />
              )}
              {closed.length > 0 && (
                <>
                  <div className="sect">Closed this week</div>
                  <div className={s.cardList}>
                    {closed.map((t) => (
                      <Link key={t.id} to={`/scan/transfer/${t.id}/in`} className={`${s.tCard} ${s.tClosed}`}>
                        <span className={s.cardTop}>
                          <span className={`mono ${s.cardCode}`}>{t.code}</span>
                          {t.totals.missing > 0 ? (
                            <span className="chip warn">
                              <span className="dot" />
                              {`${t.totals.missing} missing`}
                            </span>
                          ) : (
                            <span className="chip ok">All in</span>
                          )}
                          <span className={`mono ${s.cardTally}`}>
                            {t.totals.received}/{t.totals.sent}
                          </span>
                        </span>
                        <span className={s.cardSays}>
                          {`From ${siteById.get(t.fromSiteId)?.name ?? 'another site'} · closed ${t.closedAt ? when(t.closedAt, tz) : ''}`}
                        </span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </Listing>
          ) : (
            <Listing query={drafts} what="drafts">
              <div className="sect">Not sent yet</div>
              {(drafts.data ?? []).length === 0 ? (
                <Empty title="No drafts" description={`Start a new transfer to send boxes from ${site.name}.`} />
              ) : (
                <div className={s.cardList}>
                  {drafts.data!.map((t) => (
                    <Link key={t.id} to={`/scan/transfer/${t.id}/out`} className={s.tCard}>
                      <span className={s.cardTop}>
                        <span className={`mono ${s.cardCode}`}>{t.code}</span>
                        <span className="chip">Draft</span>
                        <RouteTags from={siteById.get(t.fromSiteId)} to={siteById.get(t.toSiteId)} />
                      </span>
                      <span className={s.cardSays}>
                        {`To ${siteById.get(t.toSiteId)?.name ?? 'another site'} · ${t.totals.picked === null ? 'Counting · hidden' : `${plural(t.totals.picked, 'box', 'boxes')} scanned`}`}
                      </span>
                      <SkuLines transfer={t} count={(l) => l.picked ?? 0} skuById={skuById} />
                    </Link>
                  ))}
                </div>
              )}
            </Listing>
          )}
        </div>
      </div>

      {/* On both tabs: the screen can open on Incoming with nothing on its way, and starting a
          transfer must not hide behind a tab the worker has to think to press. */}
      <div className="scan-foot">
        <Link to="/scan/transfer/new" className={`bigbtn accent ${s.newLink}`}>
          New transfer
        </Link>
      </div>
    </>
  );
}

/** A list query's three states: first load, a load that never worked, and the list itself. */
function Listing({ query, what, children }: { query: ReturnType<typeof useTransfers>; what: string; children: ReactNode }) {
  if (query.isPending) {
    return (
      <div className={kit.tkCentre}>
        <Spinner label={`Loading ${what}`} />
      </div>
    );
  }
  // A refresh that fails keeps the list on screen with a RefetchNote; only a list that never loaded
  // takes the panel.
  if (query.isLoadingError) {
    return (
      <div className={kit.tkStack}>
        <ErrorNote error={query.error} />
        <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void query.refetch()}>
          Try again
        </button>
      </div>
    );
  }
  return (
    <>
      <RefetchNote queries={[query]} />
      {children}
    </>
  );
}

function SkuLines({ transfer, count, skuById }: { transfer: TransferDto; count: (l: TransferDto['lines'][number]) => number; skuById: ReturnType<typeof useSkuMap> }) {
  const lines = transfer.lines.filter((l) => count(l) > 0);
  if (lines.length === 0) return null;
  return (
    <span className={s.skuLines}>
      {lines.map((l) => (
        <span key={l.skuId} className={s.skuLine}>
          <span className={`skux ${s.skuCell}`}>
            <SkuTag sku={skuById.get(l.skuId)} />
          </span>
          <span className={`mono ${s.skuTimes}`}>×{count(l)}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * Where the boxes are going. Every other site is a tile; one that is not live yet says what that
 * means for the boxes rather than being hidden, because sending stock ahead to a site that is still
 * being set up is allowed (§6.6).
 */
export function TransferNew() {
  const { site } = useScanSite();
  const sites = useSites();
  const qc = useQueryClient();
  const nav = useNavigate();

  const create = useMutation({
    mutationFn: (to: SiteDto) => api.post<TransferDetailDto>('/transfers', { fromSiteId: site!.id, toSiteId: to.id }),
    onSuccess: (dto) => {
      qc.setQueryData(qk.transfer(dto.id), dto);
      void qc.invalidateQueries({ queryKey: ['transfers'] });
      nav(`/scan/transfer/${dto.id}/out`, { replace: true });
    },
  });

  if (!site) {
    return (
      <div className="scan-body">
        <Empty title="No site on this account" description="Transfers work from a site. Ask an admin to add you to one." />
      </div>
    );
  }

  const others = (sites.data ?? []).filter((x) => x.id !== site.id);

  return (
    <div className={`scan-body ${s.newBody}`}>
      <p className={s.newFrom}>
        <SiteTag site={site} />
        {`From ${site.name}`}
      </p>
      <div className="sect">Send to</div>
      <RefetchNote queries={[sites]} />
      {sites.isPending ? (
        <div className={kit.tkCentre}>
          <Spinner label="Loading sites" />
        </div>
      ) : sites.isLoadingError ? (
        <div className={kit.tkStack}>
          <ErrorNote error={sites.error} />
          <button type="button" className={`bigbtn ghost ${kit.tkRetry}`} onClick={() => void sites.refetch()}>
            Try again
          </button>
        </div>
      ) : others.length === 0 ? (
        <Empty title="No other sites" description="A transfer needs a second site to send to." />
      ) : (
        <div className={s.destList}>
          {others.map((x) => (
            <button key={x.id} type="button" className={s.destTile} disabled={create.isPending} onClick={() => create.mutate(x)}>
              <SiteTag site={x} />
              <span className={s.destText}>
                <b>{x.name}</b>
                <small className={x.live ? undefined : s.destOff}>{x.live ? 'Live' : 'Not live yet: boxes will wait in transit'}</small>
              </span>
              {create.isPending && create.variables?.id === x.id ? <Spinner label="Starting the transfer" /> : <Icon name="chev-right" size={20} />}
            </button>
          ))}
        </div>
      )}
      <ErrorNote error={create.error} />
    </div>
  );
}
